import type { Material, Object3D } from 'three';

import type { AnimationManager } from '../../../three-helpers/animation';
import { easeOutCubic } from '../../../three-helpers/animation/easing';
import {
	prepareMaterialsForFade,
	prepareShadowsForFade,
	restoreMaterials,
	restoreShadows,
	setObjectOpacity,
	setObjectShadows
} from '../../../three-helpers/animation/transition-helpers/material-state';

const LAYER_FADE_DURATION = 400;
const SHADOW_THRESHOLD = 0.5;

/**
 * Collect all materials from a mesh-like Object3D
 */
const collectMaterials = (object: Object3D): Array<Material> => {
	const materials: Array<Material> = [];
	object.traverse((child) => {
		if ((child as any).isMesh && (child as any).material) {
			const mat = (child as any).material;
			if (Array.isArray(mat)) {
				materials.push(...mat);
			} else {
				materials.push(mat);
			}
		}
	});
	return materials;
};

/**
 * Animate a single object's opacity for a fade-in transition
 */
const fadeInObject = (child: Object3D, animId: string, animationManager: AnimationManager): Promise<void> => {
	child.visible = true;
	prepareMaterialsForFade(child);
	prepareShadowsForFade(child);
	setObjectOpacity(child, 0);
	setObjectShadows(child, false);

	return animationManager.animateOpacity(animId, 0, 1, LAYER_FADE_DURATION, (opacity) => {
		setObjectOpacity(child, opacity);
		if (opacity >= SHADOW_THRESHOLD) setObjectShadows(child, true);
	}, {
		easing: easeOutCubic,
		onComplete: () => {
			restoreMaterials(child);
			setObjectOpacity(child, 1);
			restoreShadows(child);
		}
	});
};

/**
 * Animate a single object's opacity for a fade-out transition
 */
const fadeOutObject = (child: Object3D, animId: string, animationManager: AnimationManager): Promise<void> => {
	prepareMaterialsForFade(child);
	prepareShadowsForFade(child);

	return animationManager.animateOpacity(animId, 1, 0, LAYER_FADE_DURATION, (opacity) => {
		setObjectOpacity(child, opacity);
		if (opacity < SHADOW_THRESHOLD) setObjectShadows(child, false);
	}, {
		easing: easeOutCubic,
		onComplete: () => {
			child.visible = false;
			restoreMaterials(child);
			restoreShadows(child);
		}
	});
};

/**
 * Fade out a co-located mesh that overlaps another opaque mesh.
 * Disables depthWrite on materials during the fade so the opaque mesh
 * behind it is visible as the outgoing becomes transparent.
 */
const fadeOutOverlapping = (child: Object3D, animId: string, animationManager: AnimationManager): Promise<void> => {
	prepareMaterialsForFade(child);
	prepareShadowsForFade(child);

	// Disable depthWrite so the opaque incoming mesh renders through
	const materials = collectMaterials(child);
	const savedDepthWrite = materials.map((m) => (m as any).depthWrite as boolean);
	for (const mat of materials) {
		(mat as any).depthWrite = false;
	}

	return animationManager.animateOpacity(animId, 1, 0, LAYER_FADE_DURATION, (opacity) => {
		setObjectOpacity(child, opacity);
		if (opacity < SHADOW_THRESHOLD) setObjectShadows(child, false);
	}, {
		easing: easeOutCubic,
		onComplete: () => {
			child.visible = false;
			// Restore depthWrite before restoring materials
			materials.forEach((mat, i) => { (mat as any).depthWrite = savedDepthWrite[i]; });
			restoreMaterials(child);
			restoreShadows(child);
		}
	});
};

/**
 * Collect all scene children matching a userData.type
 */
const collectByType = (objectHolder: Object3D, type: string): Array<Object3D> => {
	const matches: Array<Object3D> = [];
	objectHolder.traverse((child) => {
		if (child.userData?.type === type) {
			matches.push(child);
		}
	});
	return matches;
};

/**
 * Swap a pair of with-cutout / without-cutout meshes with a fade-out transition.
 * The incoming mesh is shown instantly (opaque, behind the outgoing), then the
 * outgoing fades out with depthWrite disabled so the incoming shows through.
 */
const swapCutoutPair = (
	outgoing: Array<Object3D>,
	incoming: Array<Object3D>,
	animationManager: AnimationManager
): void => {
	if (incoming.length === 0) return;

	// Skip if the outgoing layer is hidden (e.g. countertops toggled off)
	const anyOutgoingVisible = outgoing.some((c) => c.visible);
	if (!anyOutgoingVisible) return;

	// Show incoming instantly — it sits behind the still-opaque outgoing
	for (const child of incoming) {
		child.visible = true;
		setObjectOpacity(child, 1);
	}

	// Fade out outgoing with depthWrite disabled to reveal the incoming underneath
	for (const child of outgoing) {
		fadeOutOverlapping(child, `layer-fade-cutout-${child.uuid}`, animationManager);
	}
};

/**
 * Collect paired with-cutout / without-cutout meshes that share the same parent island.
 * Only returns meshes on islands that actually have cutout clones.
 */
const collectCutoutPairs = (
	objectHolder: Object3D, withType: string, withoutType: string
): { withCutouts: Array<Object3D>; withoutCutouts: Array<Object3D> } => {
	const withoutCutouts = collectByType(objectHolder, withoutType);
	if (withoutCutouts.length === 0) return { withCutouts: [], withoutCutouts: [] };

	// Build a set of parent islands that have no-cutout clones
	const islandsWithClones = new Set(withoutCutouts.map((c) => c.parent).filter(Boolean));

	// Only include with-cutout meshes whose parent is in that set
	const withCutouts = collectByType(objectHolder, withType)
		.filter((c) => islandsWithClones.has(c.parent));

	return { withCutouts, withoutCutouts };
};

/**
 * Cross-fade cutout visibility by swapping between with-cutout and without-cutout
 * meshes for both countertops and panels.
 * Only operates on islands that actually contain cutouts.
 *
 * showCutouts=true:  fade in cut surfaces (with holes), fade out solid surfaces
 * showCutouts=false: fade in solid surfaces, fade out cut surfaces (with holes)
 */
const crossFadeCutouts = (objectHolder: Object3D, visible: boolean, animationManager: AnimationManager): void => {
	const countertops = collectCutoutPairs(objectHolder, 'countertop', 'countertop-no-cutout');
	const panels = collectCutoutPairs(objectHolder, 'panel', 'panel-no-cutout');

	if (visible) {
		swapCutoutPair(countertops.withoutCutouts, countertops.withCutouts, animationManager);
		swapCutoutPair(panels.withoutCutouts, panels.withCutouts, animationManager);
	} else {
		swapCutoutPair(countertops.withCutouts, countertops.withoutCutouts, animationManager);
		swapCutoutPair(panels.withCutouts, panels.withoutCutouts, animationManager);
	}
};

export type SetLayerVisibilityParams = {
	type: string;
	visible: boolean;
	objectHolder: Object3D;
	animationManager: AnimationManager | null;
};

/**
 * Toggle visibility of scene objects by their userData.type with fade transitions.
 * This avoids a full scene rebuild when toggling layer options.
 */
export const setLayerVisibilityCfn = (params: SetLayerVisibilityParams): void => {
	const { type, visible, objectHolder, animationManager } = params;

	// Cutouts are a special case: cross-fade between with/without cutout geometry
	if (type === 'cutout') {
		if (animationManager) {
			crossFadeCutouts(objectHolder, visible, animationManager);
		}
		return;
	}

	const matches = collectByType(objectHolder, type);

	// When hiding countertop or panel layers, also hide the no-cutout clones
	// so they stay in sync with the layer visibility
	const noCutoutType = (type === 'countertop' || type === 'panel') ? `${type}-no-cutout` : null;
	const noCutoutMatches = noCutoutType ? collectByType(objectHolder, noCutoutType) : [];

	if (matches.length === 0 && noCutoutMatches.length === 0) return;

	// Fallback: no animation manager -> instant toggle
	if (!animationManager) {
		for (const child of matches) {
			child.visible = visible;
		}
		if (!visible) {
			for (const child of noCutoutMatches) {
				child.visible = false;
			}
		}
		return;
	}

	// Fade primary meshes
	for (const child of matches) {
		const animId = `layer-fade-${type}-${child.uuid}`;

		if (visible) {
			fadeInObject(child, animId, animationManager);
		} else {
			fadeOutObject(child, animId, animationManager);
		}
	}

	// When hiding, also fade out any visible no-cutout clones
	if (!visible) {
		for (const child of noCutoutMatches) {
			if (child.visible) {
				fadeOutObject(child, `layer-fade-${type}-noc-${child.uuid}`, animationManager);
			}
		}
	}
};
